import { Auth0ContextInterface, User } from '@auth0/auth0-react';
import logger from './services/loggerManager';
import { SecureHttpClientConfig } from './SecureDataProvider';
import { ColumnOptions, useRestGrid } from '@chargeafter/providers';
import { environment } from './environment';

type FormErrorResponse = {
  metadata: {
    propertyName: string;
  };
  description: string;
};

const parseJsonWithDate = <T>(jsonString: string): T => {
  const reDateDetect = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/; // startswith: 2015-04-29T22:06:55
  const resultObject = JSON.parse(jsonString, (_: string, value: unknown) => {
    if (typeof value == 'string' && reDateDetect.test(value)) {
      return new Date(value);
    }
    return value;
  });
  return resultObject as T;
};

export const formatErrorsResp = (errors: FormErrorResponse[]) => {
  return (errors as FormErrorResponse[]).reduce(
    (acc, curr: FormErrorResponse) => {
      acc[curr.metadata.propertyName] = curr.description;
      return acc;
    },
    {} as Record<string, string>,
  );
};

const gatewayBaseUrl = environment.backendBaseUrl;
const operateBaseUrl = environment.backendBaseUrl + '/v2/operate';
const userManagerBaseUrl = environment.backendBaseUrl + '/v2/user-manager';

let config: SecureHttpClientConfig | undefined;
let getAuth0Token: Auth0ContextInterface<User>['getAccessTokenSilently'];

// t - stands for temp
export const initializeSecureHttpClientConfig = (
  tgetAuth0Token: Auth0ContextInterface<User>['getAccessTokenSilently'],
  tConfig?: SecureHttpClientConfig,
) => {
  getAuth0Token = tgetAuth0Token;
  config = tConfig;
};

const stringify = <T>(data: T) => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    return '';
  }
};

const fetchMiddleware = async <R, T = R>({
  path,
  method,
  body,
  fileName,
  fetchingFunction,
  publicKey,
  options = {},
  additionalHeaders = {},
}: {
  path: string;
  method: 'POST' | 'GET' | 'PUT' | 'DELETE';
  body?: object;
  fileName: string;
  fetchingFunction: string;
  publicKey?: string;
  options?: { isFormRequest?: boolean };
  additionalHeaders?: Record<string, string>;
}) => {
  const { isFormRequest } = options;
  if (!getAuth0Token) {
    throw new Error('Fetcher is not initialized');
  }

  const token = publicKey ?? (await getAuth0Token());
  try {
    logger.log(`Send ${method} request to ${path}`, {
      filename: fileName,
      function: fetchingFunction,
      ...(body && { body }),
    });
    const response = await fetch(path, {
      method,
      body: body ? stringify(body) : null,
      headers: {
        ...additionalHeaders,
        ...(config?.merchantId && { 'x-ca-impersonate': config.merchantId }),
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      logger.error(`Raw response of ${method} request to ${path}`, {
        filename: fileName,
        function: fetchingFunction,
        response: JSON.stringify(response),
      });
    }
    try {
      const resp = await (!response.ok ? response.json() : response.text());
      logger.log(`Received ${method} response from ${path}`, {
        filename: fileName,
        function: fetchingFunction,
        statusCode: response.status,
        response: JSON.stringify(resp),
      });

      if (isFormRequest && resp.errors) {
        return formatErrorsResp(
          resp.errors as FormErrorResponse[],
        ) as unknown as T;
      }
      if (resp.errors) {
        throw new Error(JSON.stringify(resp.errors || resp));
      }

      if (!response.ok) {
        throw response;
      }
      // response is ok return dto or undefined according to the apo
      return resp ? parseJsonWithDate<T>(resp) : (undefined as unknown as T);
    } catch (e) {
      logger.error(`parse of ${method} request to ${path} failed`, {
        filename: fileName,
        function: fetchingFunction,
        statusCode: response.status,
        error: e,
      });
      throw e;
    }
  } catch (error) {
    logger.error(`Raw error of ${method} request to ${path}`, {
      filename: fileName,
      function: fetchingFunction,
      error: JSON.stringify(error),
    });
    if (error instanceof Response) {
      throw new Error(`${error.status} ${error.statusText}`);
    } else {
      const ex = error as Error;
      logger.error(`${method} request to ${path} failed`, {
        filename: fileName,
        function: fetchingFunction,
        error,
      });

      const errorMsg = JSON.parse(ex.message);
      if (errorMsg && errorMsg.length && errorMsg[0].code) {
        throw errorMsg[0];
      }
      throw error;
    }
  }
};

type LogInfo = {
  fileName: string;
  fetchingFunction: string;
};

const postGateway = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload: object,
  publicKey?: string,
) => {
  const path = `${gatewayBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'POST',
    body: payload,
    ...logInfo,
    publicKey,
  });
};

const getGateway = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload?: object,
) => {
  const path = `${gatewayBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({ path, method: 'GET', body: payload, ...logInfo });
};

const getOperate = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload?: object,
) => {
  const path = `${operateBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'GET',
    body: payload,
    ...logInfo,
  });
};

const postOperate = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload: object,
) => {
  const path = `${operateBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'POST',
    body: payload,
    ...logInfo,
  });
};

const putOperate = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload: object,
  options?: { isFormRequest?: boolean },
) => {
  const path = `${operateBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'PUT',
    body: payload,
    ...logInfo,
    options,
  });
};

const putUserManager = <R>(
  logInfo: LogInfo,
  relativePath: string,
  payload: object,
  options?: { isFormRequest?: boolean },
) => {
  const path = `${userManagerBaseUrl}/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'PUT',
    body: payload,
    ...logInfo,
    options,
  });
};

const sendOmniLink = async <R>(
  logInfo: LogInfo,
  payload: object,
  flow: 'apply' | 'checkout',
) => {
  const path = `${gatewayBaseUrl}/v2/omni-link/consumer/${
    flow === 'apply' ? 'application' : 'checkout'
  }`;
  const res = await fetchMiddleware<R>({
    path,
    method: 'POST',
    body: payload,
    ...logInfo,
  });
  return res;
};

export const useSecureRestGrid = (
  collection: string,
  relativePath: string,
  getColumns: (collection: string) => ColumnOptions[],
  method: 'GET' | 'POST' | 'PUT' = 'GET',
) => {
  return useRestGrid({
    collection,
    getColumns,
    request: {
      url: `${environment.backendBaseUrl}/v2/operate/api/${relativePath}`,
      method,
      headers: {
        ...(config?.merchantId && { 'x-ca-impersonate': config.merchantId }),
      },
    },
  });
};

const getConfiguration = <R>(
  logInfo: LogInfo,
  relativePath: string,
  storeId: string,
) => {
  const path = `${gatewayBaseUrl}/v2/configuration/${relativePath}`;
  return fetchMiddleware<R>({
    path,
    method: 'GET',
    ...logInfo,
    //TODO: pass storeId in all requests - requires larger refactor
    additionalHeaders: {
      'x-ca-store-id': storeId,
    },
  });
};

export const secureHttpClient = {
  postGateway,
  getGateway,
  getOperate,
  putOperate,
  putUserManager,
  postOperate,
  sendOmniLink,
  getConfiguration,
};
